import {
  useGetConsultantProfileNewQuery,
  useCanDuplicateConsultantToTenantLazyQuery,
  CanDuplicateConsultantToTenant,
  useDuplicateConsultantToTenantMutation
} from "@bonliva-crm/graphql";
import { WithAlertDialog, bonlivaPipelineTheme } from "@bonliva-crm/ui";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ThemeProvider } from "@material-ui/core/styles";
import React, { useEffect, useMemo, useState } from "react";
import { ActionButtons } from "./action-buttons/action-buttons";
import { ConsultantProfileLayout } from "./consultant-profile-pipeline-layout";
import { PersonalInfoCard } from "./personal-information/personal-info-card";
import { Switches } from "./switches/switches-card";
import { TabName } from "./utils";
import { ConsultantEditorLocation, useNavigation } from "@navigation";
import { Box, Button, Typography } from "@material-ui/core";
import { store } from "@store";
import { CallListModal } from "./call-list/call-list-modal";
import { WorkExperienceModal } from "./work-experience/work-experience-modal";
import { CreatePresentationModal } from "./create-presentation/create-presentation-modal";
import { CreateOpportunityFormModal } from "../../consultant-pipeline/components/create-opportunity/create-opportunity-form-modal";
import { useDialog, useSnackbars } from "@hooks";
import { ReferenceSendToMedloModal } from "@consultants/consultant-profile-new/references/reference-send-to-medlo-modal";
import locale from "@bonliva-crm/locale";
import { Tenant } from "@bonliva-crm/shared";
import { WithCopyToClipboard, WithTooltip } from "@common";
import { OkCancelDialogOptions } from "@containers";

type Props = {
  consultantId: string;
};

export function ConsultantProfilePipeline({ consultantId }: Props) {
  const query = useGetConsultantProfileNewQuery({
    variables: {
      id: consultantId
    },
    fetchPolicy: "cache-and-network"
  });
  const snackbars = useSnackbars();

  const [getCanDuplicate, canDuplicateResult] =
    useCanDuplicateConsultantToTenantLazyQuery();
  const [duplicateConsultantToTenant] = useDuplicateConsultantToTenantMutation({
    refetchQueries: [CanDuplicateConsultantToTenant.OperationName],
    onCompleted: (e) => {
      console.log("result", e);
      snackbars.showSuccess(`${locale.Created}: ${e.duplicateConsultantToTenant}`);
      window.open(e.duplicateConsultantToTenant, "_blank");
    },
    onError: (e) => {
      console.error(e);
      snackbars.showError(e.message);
    }
  });

  const navigation = useNavigation();
  const [tab, setTab] = useState<TabName>("eventLog");
  const [callListModalOpen, setCallListModalOpen] = useState(false);
  const [workExpModalOpen, setWorkExpModalOpen] = useState(false);
  const [createPresentationModalOpen, setCreatePresentationModalOpen] = useState(false);
  const [createOppModalOpen, setCreateOppModalOpen] = useState(false);
  const sendToMedloDialog = useDialog();

  useEffect(() => {
    const { state } = store().routing.location as {
      state?: { consultantProfileTab?: string };
    };
    const prevTab = state?.consultantProfileTab;

    if (prevTab && prevTab !== "eventLog") {
      setTab(prevTab as TabName);
      store().routing.location.state = undefined;
    }
  }, []);

  useEffect(() => {
    if (query.data) {
      if (query.data.consultantById?.SocialSecurityNo) {
        getCanDuplicate({
          variables: {
            ssn: query.data.consultantById.SocialSecurityNo,
            email: query.data.consultantById.Email,
            phone: query.data.consultantById.CellPhone
          }
        });
      }
    }
  }, [query.data]);

  const personalInfo = useMemo(
    () => (
      <PersonalInfoCard
        consultantId={consultantId}
        onEdit={() => {
          navigation.navigate(new ConsultantEditorLocation(consultantId));
        }}
      />
    ),
    [navigation, consultantId]
  );

  const handleDuplicate = async (tenant: string) => {
    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions())
      .then((value) => {
        if (value === "cancel") {
          return;
        }
        return duplicateConsultantToTenant({
          variables: { tenant: tenant as Tenant, consultantId }
        });
      });
  };

  const renderCanDuplicateBtns = () => {
    if (!canDuplicateResult.data) {
      return null;
    }
    return (
      <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
        {canDuplicateResult.data?.consultantCanDuplicateToTenants.items?.map((item) => (
          <WithTooltip
            interactive
            title={
              item.canDuplicate ? null : (
                <>
                  {!item.canDuplicate && item.links.length === 0 && (
                    <Typography>{locale.NoAccess}</Typography>
                  )}
                  {item.links?.map((url, i) => (
                    <WithCopyToClipboard contentToCopy={url}>
                      <a
                        href={url}
                        target={"_blank"}
                        rel="noreferrer"
                        style={{ color: "inherit" }}
                      >
                        {`Konsult ${i + 1}`}
                      </a>
                    </WithCopyToClipboard>
                  ))}
                </>
              )
            }
          >
            <Button
              key={item.tenant}
              variant="outlined"
              color="primary"
              style={
                item.canDuplicate
                  ? {
                      background: item.tenant === "bonliva-ab" ? "#ff857a" : "#003A5B",
                      color: "#fff"
                    }
                  : null
              }
              onClick={() => handleDuplicate(item.tenant)}
              disabled={!item.canDuplicate}
            >
              {locale.tenantsShort[item.tenant]}
              {"+"}
            </Button>
          </WithTooltip>
        ))}
      </div>
    );
  };

  return (
    <ThemeProvider theme={bonlivaPipelineTheme}>
      <WithAlertDialog>
        <Box fontFamily={bonlivaPipelineTheme.typography.fontFamily}>
          {query.loading && (
            <Box
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}

          {query.data && (
            <ConsultantProfileLayout
              consultantId={consultantId}
              isUnfit={query.data.consultantById.Unfit}
              actionButtons={
                <ActionButtons
                  consultantId={consultantId}
                  unfit={query.data.consultantById.Unfit}
                  onOpenCallListModal={() => setCallListModalOpen(true)}
                  onOpenWorkExperienceModal={() => setWorkExpModalOpen(true)}
                  onCreateOpportunity={() => setCreateOppModalOpen(true)}
                  onCreatePresentation={() => setCreatePresentationModalOpen(true)}
                  onSendToMedlo={() => sendToMedloDialog.onOpen()}
                  canDuplicateBtns={renderCanDuplicateBtns()}
                />
              }
              personalInformation={personalInfo}
              switches={<Switches consultantId={consultantId} />}
              tab={tab}
              setTab={setTab}
            />
          )}

          <CallListModal
            open={callListModalOpen}
            consultantId={consultantId}
            onClose={() => setCallListModalOpen(false)}
          />

          <WorkExperienceModal
            open={workExpModalOpen}
            consultantId={consultantId}
            onClose={() => setWorkExpModalOpen(false)}
          />

          <CreatePresentationModal
            open={createPresentationModalOpen}
            consultantId={consultantId}
            onClose={() => setCreatePresentationModalOpen(false)}
          />

          <CreateOpportunityFormModal
            open={createOppModalOpen}
            consultantId={consultantId}
            onClose={() => setCreateOppModalOpen(false)}
          />

          <ReferenceSendToMedloModal
            open={sendToMedloDialog.open}
            setOpen={() => sendToMedloDialog.onClose()}
            consultantId={consultantId}
          />
        </Box>
      </WithAlertDialog>
    </ThemeProvider>
  );
}
